import {
  Accordion,
  Text,
  useMantineTheme,
  ActionIcon,
  Flex,
  Loader,
  Title,
  Group,
  Button,
} from "@mantine/core";
import { IconCirclePlus, IconPencil, IconWifi } from "@tabler/icons";
import { memo } from "react";
import {
  PaymentMethodType,
  PaymentType,
  useGetClientPaymentMethodsManyQuery,
} from "src/generated/graphql";
import { useParams } from "react-router-dom";
import { useModals } from "@mantine/modals";
import PayrocCardInput from "./payrocHostedCardInput";

import { IClientPaymentMethod } from "src/screens/Profile/PaymentMethods";
import EditCard from "./editCard";
import { BsBank, BsCreditCardFill } from "react-icons/bs";
import useWritePermission from "src/hooks/usePermission";
import { EMPLOYEE_ROLE_OBJ } from "src/utils/constants";

const ClientPaymentMethods = () => {
  const isAllowWrite = useWritePermission(EMPLOYEE_ROLE_OBJ.clients);
  const theme = useMantineTheme();
  const modals = useModals();
  const { clientId } = useParams<{ clientId: string }>();
  let color = theme.colors.secondaryTextColor[2];

  const { data, loading, refetch } = useGetClientPaymentMethodsManyQuery({
    variables: {
      clientId: clientId || "",
    },
    fetchPolicy: "network-only",
  });

  const handleEditCard = (id: string, isPrimary: boolean, isCard: boolean) => {
    modals.openModal({
      modalId: "EditPaymentMethod",
      closeOnClickOutside: false,
      title: "Edit Payment Method",
      children: (
        <EditCard
          id={id}
          isPrimary={isPrimary}
          onComplete={() => {
            refetch();
            modals.closeModal("EditPaymentMethod");
          }}
          onEditDetails={() => {
            modals.openModal({
              modalId: "secureCardInputModal",
              closeOnClickOutside: false,
              title: "Secure Card Input",
              children: (
                <PayrocCardInput
                  clientId={clientId || ""}
                  paymentMethodId={id}
                  onComplete={() => {
                    modals.closeModal("secureCardInputModal");
                    refetch();
                  }}
                  isCard={isCard}
                />
              ),
              styles: {
                modal: {
                  backgroundColor: "rgba(0,0,0,0) !important",
                },
                header: {
                  backgroundColor: theme.colors.primary[6],
                  color: theme.colors.primaryTextColor[6],
                  borderTopLeftRadius: 4,
                  borderTopRightRadius: 4,
                },
                body: {
                  color: "#222222 !important",
                  backgroundColor: "#f0f0f0",
                  borderBottomLeftRadius: 4,
                  borderBottomRightRadius: 4,
                },
              },
            });
          }}
        />
      ),
    });
  };

  const paymentCard = (item: IClientPaymentMethod, index?: number) => {
    let bg = theme.colors.primary[3];
    let textColor = theme.colors.primaryTextColor[3];
    const expiryDate = item.expiresAt && new Date(item.expiresAt);
    return (
      <Flex
        key={item?.id}
        style={{
          width: "350px",
          height: "190px",
          backgroundColor: bg,
          color: textColor,
          borderRadius: "10px",
          padding: "10px 20px",
        }}
        direction={"column"}
        justify={"space-between"}
        mt={index || 0 > 0 ? 15 : undefined}
      >
        <Flex justify={"space-between"}>
          <Text size={20} fw={500}>
            {item.type === PaymentMethodType.Card
              ? item?.merchant || ""
              : `ACH - ${item.routingNumber || ""}`}
          </Text>
          {isAllowWrite ? (
            <ActionIcon
              size="md"
              variant="transparent"
              onClick={() =>
                handleEditCard(
                  item.id,
                  item.isPrimary,
                  item.type === PaymentMethodType.Card
                )
              }
              sx={{
                "&:hover": {
                  backgroundColor: theme.colors.primary[0],
                },
              }}
            >
              <IconPencil color={textColor} />
            </ActionIcon>
          ) : null}
        </Flex>

        <Flex direction={"column"}>
          <Flex pb={20} justify={"space-between"}>
            <Text size={20} fw={500}>
              {`${
                item.type === PaymentMethodType.Card
                  ? "**** **** ****"
                  : `Ending in`
              } ${item.mask?.substring(item.mask?.length - 4)}`}
            </Text>
            {item.type === PaymentMethodType.Card && (
              <IconWifi style={{ transform: "rotate(90deg)" }} />
            )}
          </Flex>
          <Flex align="center" justify={"space-between"} pb={10}>
            <Text size={18} fw={500}>
              {item.name}
            </Text>
            {expiryDate && (
              <Text size={14}>
                Exp : {expiryDate.getUTCMonth()} / {expiryDate.getUTCFullYear()}
              </Text>
            )}
          </Flex>
        </Flex>
      </Flex>
    );
  };

  const newCard = async (isCard: boolean) => {
    modals.openModal({
      modalId: "secureCardInputModal",
      closeOnClickOutside: false,
      title: "Secure Card Input",
      children: (
        <PayrocCardInput
          clientId={clientId || ""}
          onComplete={() => {
            modals.closeModal("secureCardInputModal");
            refetch();
          }}
          isCard={isCard}
        />
      ),
      styles: {
        modal: {
          backgroundColor: "rgba(0,0,0,0) !important",
        },
        header: {
          backgroundColor: theme.colors.primary[6],
          color: theme.colors.primaryTextColor[6],
          borderTopLeftRadius: 4,
          borderTopRightRadius: 4,
        },
        body: {
          color: "#222222 !important",
          backgroundColor: "#f0f0f0",
          borderBottomLeftRadius: 4,
          borderBottomRightRadius: 4,
        },
      },
    });
  };

  const selectPaymentType = () => {
    modals.openModal({
      modalId: "selectNewPaymentType",
      closeOnClickOutside: false,
      title: "New Payment Method",
      children: (
        <Group spacing={20} align="center">
          <Flex
            onClick={() => newCard(true)}
            bg={theme.colors.primary[6]}
            sx={{
              //border: `1px solid #DDDDDD`,
              width: 180,
              height: 180,
              borderRadius: 4,
              cursor: "pointer",
            }}
            align={"center"}
            direction={"column"}
            justify={"center"}
          >
            <BsCreditCardFill
              size={50}
              color={theme.colors.primaryTextColor[6]}
            />
            <Text size={24} fw={500} color={theme.colors.primaryTextColor[6]}>
              Credit/Debit
            </Text>
          </Flex>
          <Flex
            onClick={() => newCard(false)}
            bg={theme.colors.primary[6]}
            sx={{
              //border: `1px solid #DDDDDD`,
              width: 180,
              height: 180,
              borderRadius: 4,
              cursor: "pointer",
            }}
            align={"center"}
            direction={"column"}
            justify={"center"}
          >
            <BsBank size={50} color={theme.colors.primaryTextColor[6]} />
            <Text size={24} fw={500} color={theme.colors.primaryTextColor[6]}>
              Bank Account
            </Text>
          </Flex>
        </Group>
      ),
      styles: {
        modal: {
          backgroundColor: "rgba(0,0,0,0) !important",
        },
        header: {
          backgroundColor: theme.colors.primary[6],
          color: theme.colors.primaryTextColor[6],
          borderTopLeftRadius: 4,
          borderTopRightRadius: 4,
        },
        body: {
          color: theme.colors.secondaryTextColor[6],
          backgroundColor: theme.colors.secondary[6],
          borderBottomLeftRadius: 4,
          borderBottomRightRadius: 4,
        },
      },
    });
  };

  return (
    <>
      <Flex
        align={"center"}
        justify={"space-between"}
        style={{ paddingBottom: "12.5px", borderBottom: "1px solid" }}
      >
        <Title order={4} color={color}>
          Payment Methods
        </Title>
        {isAllowWrite && (
          <ActionIcon
            size="lg"
            onClick={() => {
              selectPaymentType();
            }}
            disabled={!clientId}
            sx={{
              "&:hover": {
                backgroundColor: theme.colors.secondary[0],
              },
            }}
          >
            <IconCirclePlus color={color} />
          </ActionIcon>
        )}
      </Flex>
      <Accordion
        multiple
        defaultValue={["primary", "other"]}
        transitionDuration={500}
        styles={{
          item: {
            border: `1px solid ${theme.colors.primary[6]}`,
            marginTop: 15,
            borderRadius: "4px",
            overflow: "hidden",
          },
          control: {
            background: theme.colors.primary[6],
            color: theme.colors.primaryTextColor[6],
            padding: "5px 13px",
            "&:hover": {
              backgroundColor: `${theme.colors.primary[4]} !important`,
            },
          },
          label: {
            fontWeight: 500,
            fontSize: 16,
          },
          panel: {
            paddingTop: 10,
            backgroundColor: theme.colors.secondary[6],
          },
        }}
      >
        <Accordion.Item value="primary">
          <Accordion.Control>Primary</Accordion.Control>
          <Accordion.Panel>
            <Flex justify={"center"} py={10} direction="column">
              {!loading ? (
                (
                  data?.clientPaymentMethodsMany?.filter(
                    (method) => method.isComplete
                  ) as IClientPaymentMethod[]
                )?.map((item) => item?.isPrimary && paymentCard(item))
              ) : (
                <Loader />
              )}
            </Flex>
          </Accordion.Panel>
        </Accordion.Item>

        <Accordion.Item value="other">
          <Accordion.Control>Other</Accordion.Control>
          <Accordion.Panel>
            <Flex justify={"center"} py={10} direction="column">
              {!loading ? (
                (
                  data?.clientPaymentMethodsMany?.filter(
                    (method) => method.isComplete
                  ) as IClientPaymentMethod[]
                )?.map(
                  (item, index) => !item?.isPrimary && paymentCard(item, index)
                )
              ) : (
                <Loader />
              )}
            </Flex>
          </Accordion.Panel>
        </Accordion.Item>
      </Accordion>
    </>
  );
};

export default memo(ClientPaymentMethods);
